import { toast } from "react-toastify";
import { getTokenObj } from "../../Services/tokenManager";

/**
 * Format reschedule date from API format to user-friendly format with timezone
 * @param {string} dateStr - Date string in "06/04/2026 03:30:00 PM" format
 * @param {string} timezoneValue - Timezone value (e.g., "Asia/Calcutta")
 * @param {Array} timeZoneList - List of timezone objects with value and timeZones properties
 * @returns {string} Formatted date string (e.g., "June 4, 03:30 PM IST")
 */
export const formatRescheduleDate = (dateStr, timezoneValue, timeZoneList) => {
  if (!dateStr) return "";
  try {
    // Parse "06/04/2026 03:30:00 PM" format
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr;

    const monthNames = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];
    const day = date.getDate();
    const month = monthNames[date.getMonth()];
    const hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    const hours12 = hours % 12 || 12;

    // Get timezone abbreviation
    const tzFound = timeZoneList?.find((tz) => tz?.value === timezoneValue);
    const tzMatch = tzFound?.timeZones?.match(/\(([^)]+)\)/);
    const tzAbbr = tzMatch ? tzMatch[1] : "";

    return `${month} ${day}, ${hours12}:${minutes} ${ampm}${tzAbbr ? " " + tzAbbr : ""}`;
  } catch (e) {
    return dateStr;
  }
};

/**
 * Whether the given subject is Math (used to gate the placement/evaluation test
 * recommendation, which is only available for Math).
 * @param {string} subject - Subject/course name (e.g., "Math", "Coding")
 * @returns {boolean}
 */
export const isMathSubject = (subject) =>
  typeof subject === "string" && subject.toLowerCase().includes("math");

/**
 * Opens the assessment-test-web (Ark FE / noah-2 assessments) at the given route,
 * bridging the cy-sso tokens through its auth callback.
 *
 * The Ark FE authenticates from its own httpOnly cookie, set only by its
 * `/api/auth/callback`. A direct link to an app route has no cookie and bounces, so
 * we always route through the callback with the cy-sso tokens and a `next` path.
 * Tokens come from the same place as navigateToQuiz (`getTokenObj()`).
 *
 * @param {string} nextPath - App route to land on, e.g.
 *   "/placement/new?testId=4&regionId=1" or "/homework/start?testId=7"
 * @returns {boolean} true once navigation is initiated; false if it couldn't be
 *   (e.g. the base URL isn't configured). Callers use this to keep/reset a
 *   button's loading state — navigation unloads the page, so `true` never resets.
 */
export const openAssessmentWeb = (nextPath) => {
  const { ACCESS_TOKEN, REFRESH_TOKEN } = getTokenObj() ?? {};
  const baseUrl = process.env.REACT_APP_DIAGNOSTIC_TEST_WEB_URL;
  if (!baseUrl) {
    toast.error("Assessment web URL not configured");
    console.error(
      "[openAssessmentWeb] REACT_APP_DIAGNOSTIC_TEST_WEB_URL not set",
    );
    return false;
  }
  const url =
    `${baseUrl}/api/auth/callback` +
    `?accessToken=${encodeURIComponent(ACCESS_TOKEN ?? "")}` +
    `&refreshToken=${encodeURIComponent(REFRESH_TOKEN ?? "")}` +
    `&next=${encodeURIComponent(nextPath)}`;
  window.location.assign(url);
  return true;
};

/** Open a placement/diagnostic route on the assessment web (e.g. "/placement/report/33"). */
export const openDiagnosticWeb = (nextPath) => openAssessmentWeb(nextPath);

// ── Placement test, shared by the guest dashboard's trial cards and the paid
// student's upcoming class card. `placement` is one subject's entry of the
// dashboard payload's `placements` (e.g. `placements.math`).

/** Assessment-web route that starts the placement test for `placement`. */
export const placementTestPath = (placement) =>
  `/placement/new?testId=${placement?.testId}&regionId=${placement?.regionId}&grade=${placement?.grade}`;

/** Assessment-web route of a taken placement test's report, keyed by its result's session id. */
export const placementReportPath = (sessionId) => `/placement/report/${sessionId}`;

/**
 * What a card shows for `placement`: once taken, the score (checked first, since
 * `available` only gates starting a test, not showing a result); while a test is
 * available, the start link; otherwise nothing.
 * @returns {{ isTaken: true, score: number|null, sessionId: string|null }
 *   | { isTaken: false } | null}
 */
export const getPlacementState = (placement) => {
  if (placement?.status === "taken") {
    return {
      isTaken: true,
      score: placement?.result?.score ?? null,
      sessionId: placement?.result?.sessionId ?? null,
    };
  }
  if (!placement?.available) return null;
  return { isTaken: false };
};

/**
 * Launch an AI homework session on the assessment web via its clarity screen.
 * noah-2 owns the single "homework starting" screen (`/homework/start`), which
 * resolves the config, then hands off to the runner.
 *
 * @param {string|number} testConfigId - The homework `test_config` id to run.
 * @returns {boolean} false when the assessment-web URL isn't configured.
 */
export const openHomeworkWeb = (testConfigId) =>
  openAssessmentWeb(
    `/homework/start?testId=${encodeURIComponent(testConfigId)}`,
  );

/**
 * Opens the report/summary for a completed AI homework run on the assessment web.
 * Keyed by the SESSION — the run that was completed — not by the homework's
 * config, exactly like openDiagnosticWeb(`/placement/report/${sessionId}`).
 * A completed homework with no session id has no report; don't call this for it.
 *
 * @param {string|number} sessionId - The completed run's session id.
 * @returns {boolean} false when the assessment-web URL isn't configured.
 */
export const openHomeworkReportWeb = (sessionId) =>
  openAssessmentWeb(`/homework/report/${sessionId}`);

/**
 * Launch a mentor-assigned test on the assessment web.
 *
 * Mentor-assigned tests resolve to a noah-2 *diagnostic* `test_config` (Ark's
 * `ensureDiagnosticTestConfig`), so they run through the test-session flow — NOT
 * the homework runner. `/diagnostic/new` creates the session server-side then
 * redirects into the runner, and self-heals to `/placement/new` if the config is
 * placement-mode. Same cy-sso bridge / launch-by-config-id pattern as
 * `openHomeworkWeb`; only the noah-2 entry route differs.
 *
 * @param {string|number} configId - The noah-2 test `config` id to run.
 */
export const openTestWeb = (configId) =>
  openAssessmentWeb(`/diagnostic/new?testId=${encodeURIComponent(configId)}`);

/**
 * Launch an AI practice session on the assessment web via its clarity screen.
 * Mirrors openHomeworkWeb: noah-2 owns the single "practice starting" screen
 * (`/practice/start`), which resolves the config, then hands off to the runner.
 *
 * @param {string|number} testConfigId - The practice `test_config` id to run
 *   (goal.practiceMeta.configId from the goals API).
 * @returns {boolean} false when the assessment-web URL isn't configured.
 */
export const openPracticeWeb = (testConfigId) =>
  openAssessmentWeb(
    `/practice/start?testId=${encodeURIComponent(testConfigId)}`,
  );
